/**
 * The Speedy player's state and its one history entry (F-129; A plan §2.4, §4.3; BUILD-BRIEF §10.17).
 *
 * The player is an overlay, never a route, so the page under it (and its scroll position) stays
 * mounted. Opening pushes ONE history entry carrying `speedyOverlay: <key>`; Back pops it and
 * closes. The rules, each enforced here:
 *   * `open` is ignored while open; every open gets a new key;
 *   * `requestClose` acts once (a closing flag), so Close and Escape together pop one entry;
 *   * if our entry is on top, close by going back (the history listener finishes); otherwise
 *     close directly;
 *   * the listener finishes with the exit that was requested, or `back` if none was;
 *   * `closeThen(fn)` (a link inside the player) waits for the close to land, then runs `fn`;
 *   * a page loaded with a stale `speedyOverlay` state has it removed and doesn't reopen.
 *
 * Plain code, no React, so the rules are unit tested directly.
 */
import type { SpeedyCard } from '@/api/speedies';
import type { SpeedyEntry, SpeedyExit } from '@/lib/tracking/speedy-analytics';

export type SpeedyListSource =
  | { kind: 'open_feed' }
  | { kind: 'single'; speedyId: string }
  | { kind: 'given'; items: SpeedyCard[] };

export interface OpenPlayerArgs {
  entry: SpeedyEntry;
  source: SpeedyListSource;
  /** Play this one first; otherwise the first in the list. */
  startSpeedyId?: string;
  slot?: 0 | 1;
  /** The tile tapped, 0 = first (a carousel holds up to 10). */
  tileIndex?: number;
  communityId?: string | null;
  channelId?: string | null;
  /** Shown when the last unfinished Speedy in a series or group ends. */
  celebrate?: { kind: 'series' | 'group'; name: string };
  /** Not a member viewing session (an admin reviewing): no player analytics are written. */
  untracked?: boolean;
}

export interface PlayerSessionCounters {
  openedAt: number;
  itemsViewed: number;
  itemsCompleted: number;
  skips: number;
}

export function newCounters(now = Date.now()): PlayerSessionCounters {
  return { openedAt: now, itemsViewed: 0, itemsCompleted: 0, skips: 0 };
}

export const OVERLAY_STATE_KEY = 'speedyOverlay';

/** The history operations the controller needs (the router's history in the app; a fake in tests). */
export interface OverlayHistory {
  state(): Record<string, unknown> | undefined;
  push(state: Record<string, unknown>): void;
  replace(state: Record<string, unknown>): void;
  back(): void;
  subscribe(fn: () => void): () => void;
}

export interface OverlayController {
  /** Open; returns false (and does nothing) if already open. */
  open(onClosed: (exit: SpeedyExit) => void): boolean;
  requestClose(exit: SpeedyExit): void;
  /** Close, then run `fn` once the close has landed (a link inside the player). */
  closeThen(fn: () => void): void;
  isOpen(): boolean;
  /** On load: drop a stale overlay entry left by a reload, so it never reopens. */
  clearStale(): void;
}

/** How long a close waits for the history Back before closing anyway. */
export const CLOSE_FALLBACK_MS = 800;

let keySeq = 0;
function newKey(): string {
  keySeq += 1;
  return `${Date.now().toString(36)}-${keySeq}`;
}

export function createOverlayController(history: OverlayHistory): OverlayController {
  let openKey: string | null = null;
  let closing = false;
  let requestedExit: SpeedyExit | null = null;
  let onClosedCb: ((exit: SpeedyExit) => void) | null = null;
  let unsubscribe: (() => void) | null = null;
  const afterClose: (() => void)[] = [];

  const ourEntryOnTop = () => openKey !== null && history.state()?.[OVERLAY_STATE_KEY] === openKey;

  const finish = (exit: SpeedyExit) => {
    if (openKey === null) return;
    unsubscribe?.();
    unsubscribe = null;
    openKey = null;
    closing = false;
    requestedExit = null;
    const cb = onClosedCb;
    onClosedCb = null;
    cb?.(exit);
    for (const fn of afterClose.splice(0)) fn();
  };

  const onHistory = () => {
    if (openKey !== null && !ourEntryOnTop()) finish(requestedExit ?? 'back');
  };

  return {
    open(onClosed) {
      if (openKey !== null) return false;
      openKey = newKey();
      onClosedCb = onClosed;
      history.push({ ...(history.state() ?? {}), [OVERLAY_STATE_KEY]: openKey });
      unsubscribe = history.subscribe(onHistory);
      return true;
    },
    requestClose(exit) {
      if (openKey === null || closing) return;
      closing = true;
      requestedExit = exit;
      if (ourEntryOnTop()) {
        const key = openKey;
        history.back();
        // A Back that never lands (some webviews swallow it) would leave the player stuck open:
        // close anyway after a moment if nothing has.
        setTimeout(() => {
          if (openKey === key && closing) finish(exit);
        }, CLOSE_FALLBACK_MS);
      } else {
        finish(exit);
      }
    },
    closeThen(fn) {
      if (openKey === null) {
        fn();
        return;
      }
      afterClose.push(fn);
      this.requestClose('close');
    },
    isOpen: () => openKey !== null,
    clearStale() {
      const s = history.state();
      if (s && OVERLAY_STATE_KEY in s && openKey === null) {
        const { [OVERLAY_STATE_KEY]: _drop, ...rest } = s;
        history.replace(rest);
      }
    },
  };
}
